import { useState } from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { QrCode, Copy, Check, Download, Share2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/** Shareable QR card pointing to the user's public profile (talent or creator). */
export default function ProfileQRCard({ userId, role, name, compact = false }) {
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "https://helencia.fr";
  const path = role === "talent" ? `/talents/${userId}` : `/createurs/${userId}`;
  const url = `${origin}${path}`;
  const qrSrc = `${API}/qr/profile/${userId}.png?origin=${encodeURIComponent(origin)}&scale=10`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `${name} · Helencia`, url }); } catch { /* cancelled */ }
    } else copy();
  };

  const download = async () => {
    try {
      const res = await fetch(qrSrc);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `helencia-qr-${(name || "profil").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  return (
    <div
      className={`rounded-3xl bg-[#1A1A1A] text-white overflow-hidden relative ${compact ? "p-5" : "p-6 sm:p-7"}`}
      data-testid="profile-qr-card"
    >
      <div className="absolute -top-24 -right-20 w-64 h-64 rounded-full bg-[#C68E59]/25 blur-[90px] pointer-events-none" />
      <div className="relative flex flex-col sm:flex-row gap-6 items-center sm:items-start">
        <div className="shrink-0 rounded-2xl bg-white p-3 shadow-[0_15px_40px_-15px_rgba(0,0,0,0.6)]">
          <img src={qrSrc} alt="QR code de votre profil" className="w-36 h-36 sm:w-40 sm:h-40" data-testid="profile-qr-image" />
        </div>
        <div className="flex-1 w-full min-w-0 text-center sm:text-left">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#E6C29A]">
            <QrCode className="w-3.5 h-3.5" /> Votre carte de visite
          </div>
          <h3 className="font-serif text-2xl sm:text-3xl mt-2 leading-tight">
            Partagez votre profil <span className="italic text-[#E6C29A]">en un scan</span>
          </h3>
          <p className="text-white/70 text-sm mt-2 leading-relaxed">
            Castings, défilés, rencontres : faites scanner ce QR code pour ouvrir votre profil public instantanément.
          </p>
          <div className="mt-4 flex items-center gap-2 rounded-full bg-white/8 border border-white/10 px-4 py-2 text-xs text-white/80 min-w-0">
            <ExternalLink className="w-3.5 h-3.5 shrink-0 text-[#E6C29A]" />
            <span className="truncate" data-testid="profile-qr-url">{url.replace(/^https?:\/\//, "")}</span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 justify-center sm:justify-start">
            <Button onClick={copy} size="sm" className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-9 px-4" data-testid="profile-qr-copy">
              {copied ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />} {copied ? "Copié" : "Copier le lien"}
            </Button>
            <Button onClick={download} size="sm" variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-9 px-4" data-testid="profile-qr-download">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Télécharger
            </Button>
            <Button onClick={share} size="sm" variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-9 px-4" data-testid="profile-qr-share">
              <Share2 className="w-3.5 h-3.5 mr-1.5" /> Partager
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
